import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, CheckCircle2, XCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [userInfo, setUserInfo] = useState({ email: '', first_name: '' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setTokenError('Kein Token angegeben');
      return;
    }
    
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await api.get(`/auth/verify-reset-token?token=${token}`);
      if (response.data.valid) {
        setTokenValid(true);
        setUserInfo({
          email: response.data.email,
          first_name: response.data.first_name
        });
      } else {
        setTokenError(response.data.reason || 'Ungültiger Link');
      }
    } catch (error) {
      setTokenError('Fehler beim Prüfen des Links');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', {
        token: token,
        new_password: password
      });
      
      setSuccess(true);
      toast.success('Passwort erfolgreich gesetzt!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Fehler beim Setzen des Passworts';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md" data-testid="password-set-success">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Passwort gesetzt!</h2>
            <p className="text-gray-600 mb-6">
              Sie können sich jetzt mit Ihrer E-Mail-Adresse und dem neuen Passwort anmelden.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-[#9B1B30] hover:bg-[#7A1525]"
              data-testid="go-to-login-btn"
            >
              Zur Anmeldung
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md" data-testid="token-invalid">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Link ungültig</h2>
            <p className="text-gray-600 mb-2">{tokenError}</p>
            <p className="text-sm text-gray-500 mb-6">
              Der Link ist abgelaufen oder wurde bereits verwendet. 
              Bitte fordern Sie einen neuen Link an.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/passwort-vergessen')}
                className="w-full bg-[#9B1B30] hover:bg-[#7A1525]"
              >
                Neuen Link anfordern
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zur Startseite
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="set-password-form">
        <CardHeader className="text-center">
          <div className="w-12 h-12 bg-[#9B1B30]/10 rounded-full flex items-center justify-center mx-auto mb-2">
            <Lock className="w-6 h-6 text-[#9B1B30]" />
          </div>
          <CardTitle className="text-xl">Passwort setzen</CardTitle>
          <CardDescription>
            {userInfo.first_name && (
              <span>Hallo {userInfo.first_name}, </span>
            )}
            setzen Sie Ihr persönliches Passwort für den Zugang zum Kundenportal.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>E-Mail-Adresse</Label>
              <Input 
                type="email" 
                value={userInfo.email} 
                disabled 
                className="bg-gray-100"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Neues Passwort</Label>
              <Input 
                id="password"
                type="password" 
                placeholder="Mindestens 6 Zeichen"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <Input 
                id="confirmPassword"
                type="password" 
                placeholder="Passwort wiederholen"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="confirm-password-input"
              />
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-amber-800">
                  Dieser Link kann nur einmal verwendet werden.
                </p>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-[#9B1B30] hover:bg-[#7A1525]"
              disabled={submitting}
              data-testid="set-password-btn"
            >
              {submitting ? 'Wird gespeichert...' : 'Passwort setzen'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
